import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

function formatTime(totalSeconds) {
  const seconds = Math.max(0, Number(totalSeconds || 0));
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

export default function WorkflowCleanupDialog({
  mode,
  status,
  loading,
  error,
  onDeleteNow,
  onKeepData,
  onCloseNotice,
}) {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(
    Number(status?.remaining_seconds || 0)
  );
  const [deletionProgress, setDeletionProgress] = useState(100);
  const cleanupBlocking = mode === "countdown" || mode === "deleting";

  useEffect(() => {
    setRemaining(Number(status?.remaining_seconds || 0));
  }, [status?.remaining_seconds]);

  useEffect(() => {
    if (mode !== "countdown" || remaining <= 0) return undefined;

    const timer = window.setInterval(() => {
      setRemaining((value) => Math.max(0, value - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [mode, remaining]);

  useEffect(() => {
    if (!cleanupBlocking) return undefined;

    const preventClose = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    document.documentElement.classList.add("academyCleanupLocked");
    window.addEventListener("beforeunload", preventClose);

    return () => {
      document.documentElement.classList.remove("academyCleanupLocked");
      window.removeEventListener("beforeunload", preventClose);
    };
  }, [cleanupBlocking]);

  useEffect(() => {
    if (mode !== "deleting") {
      setDeletionProgress(100);
      return undefined;
    }

    const timer = window.setInterval(() => {
      setDeletionProgress((value) => {
        if (value <= 12) return 12;
        if (value > 72) return value - 4;
        if (value > 38) return value - 2;
        return value - 1;
      });
    }, 420);

    return () => window.clearInterval(timer);
  }, [mode]);

  const countsText = useMemo(() => {
    const counts = status?.counts || {};
    const total =
      Number(counts.contacts || 0) +
      Number(counts.leads || 0) +
      Number(counts.tasks || 0) +
      Number(counts.deals || 0) +
      Number(counts.activities || 0) +
      Number(counts.files || 0) +
      Number(counts.activity_logs || 0);
    return total;
  }, [status?.counts]);

  if (!mode) return null;

  if (mode === "countdown") {
    return (
      <div className="academyCleanupLayer" aria-modal="true" role="dialog">
        <div className="academyCleanupDialog academyCleanupDialog--deleting">
          <span className="academyCleanupIcon academyCleanupIcon--timer">◷</span>
          <small>{t("academy_cleanup_scheduled")}</small>
          <h2>{t("academy_cleanup_preparing")}</h2>
          <p>{t("academy_cleanup_countdown_description")}</p>
          <strong className="academyCleanupCountdownValue">
            {formatTime(remaining)}
          </strong>
          <button
            type="button"
            className="academyCleanupNow academyCleanupSingleAction"
            onClick={onDeleteNow}
            disabled={loading}
          >
            {loading
              ? t("academy_cleanup_starting")
              : t("academy_delete_now")}
          </button>
        </div>
      </div>
    );
  }

  if (mode === "deleting") {
    const stage =
      deletionProgress > 72
        ? t("academy_cleanup_stage_prepare")
        : deletionProgress > 38
          ? t("academy_cleanup_stage_files")
          : deletionProgress > 18
            ? t("academy_cleanup_stage_entities")
            : t("academy_cleanup_stage_verify");

    return (
      <div className="academyCleanupLayer" aria-modal="true" role="dialog">
        <div
          className="academyCleanupDialog academyCleanupDialog--deleting"
          aria-busy="true"
        >
          <span className="academyCleanupIcon academyCleanupIcon--spinner">
            ↻
          </span>
          <small>{t("academy_safe_cleanup")}</small>
          <h2>{t("academy_deleting_training_data")}</h2>
          <p>{t("academy_cleanup_wait_description")}</p>

          <div className="academyCleanupProgress">
            <div className="academyCleanupProgressTop">
              <span>{stage}</span>
              <strong>{deletionProgress}%</strong>
            </div>
            <div className="academyCleanupProgressTrack">
              <span style={{ width: `${deletionProgress}%` }} />
            </div>
            <small>{t("academy_cleanup_progress_hint")}</small>
          </div>
        </div>
      </div>
    );
  }

  if (mode === "cleaned") {
    return (
      <div className="academyCleanupLayer" aria-modal="true" role="dialog">
        <div className="academyCleanupDialog academyCleanupDialog--success">
          <span className="academyCleanupIcon">✓</span>
          <h2>{t("academy_training_data_deleted")}</h2>
          <p>{t("academy_training_data_deleted_description")}</p>
          {countsText > 0 && (
            <small>
              {t("academy_deleted_records_count", { count: countsText })}
            </small>
          )}
          <button type="button" onClick={onCloseNotice}>
            {t("close")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="academyCleanupLayer" aria-modal="true" role="dialog">
      <div className="academyCleanupDialog">
        <span className="academyCleanupIcon">⌫</span>
        <small>{t("academy_practice_complete")}</small>
        <h2>{t("academy_training_data_choice")}</h2>
        <p>{t("academy_training_data_choice_description")}</p>

        <div className="academyCleanupWarning">
          {t("academy_cleanup_real_data_warning")}
        </div>

        {countsText > 0 && (
          <div className="academyCleanupSummary">
            {t("academy_found_training_records")}{" "}
            <strong>{countsText}</strong>
          </div>
        )}

        {error && <div className="academyCleanupError">{error}</div>}

        <div className="academyCleanupDialogActions">
          <button
            type="button"
            className="academyCleanupLater"
            onClick={onKeepData}
            disabled={loading}
          >
            {t("academy_keep_for_review")}
          </button>
          <button
            type="button"
            className="academyCleanupNow"
            onClick={onDeleteNow}
            disabled={loading}
          >
            {loading
              ? t("academy_deleting")
              : t("academy_delete_now")}
          </button>
        </div>
      </div>
    </div>
  );
}
